/**
 * Triggers Folder Loader - Loads triggers for a table subfolder
 */

import {
  DatabaseExplorerNode,
  MessageTreeNode,
  TriggersFolderNode,
  createTriggerNode,
  TriggerNodeMetadata,
} from '../../explorer/models/DatabaseTreeModels';
import {
  createTableSubfolderEmptyNode,
  createTableSubfolderErrorNode,
} from './objectFolderUtils';
import { getTableTriggers, TriggerInfo, formatTriggerDisplayName } from './triggersService';
import { getEngineForConnection } from './tableExpansion';

export interface LoadTriggersFolderOptions {
  node: TriggersFolderNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  forceReload?: boolean;
}

/**
 * Loads the triggers for a table when the Triggers folder is expanded
 */
export async function loadTriggersFolder({
  node,
  updateNode,
  forceReload = false,
}: LoadTriggersFolderOptions) {
  if (!forceReload && node.children && node.children.length > 0) {
    const firstChild = node.children[0];
    if (firstChild && !['message', 'error'].includes(firstChild.type)) {
      return;
    }
  }

  const { connectionId, database, schema, tableName } = node.metadata;

  if (!database) {
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, '', schema, tableName, 'triggers-folder', node.id, 'Database not specified'
        ),
      ],
      isLoading: false,
    });
    return;
  }

  updateNode(node.id, {
    children: [
      {
        id: `loading-${connectionId}-${database}-${schema}-${tableName}-triggers`,
        parentId: node.id,
        name: 'Loading triggers...',
        type: 'message',
        canHaveChildren: false,
        children: [],
        isExpanded: false,
        icon: 'loading',
        metadata: { connectionId, database },
      } as MessageTreeNode,
    ],
    isLoading: true,
  });

  try {
    const engine = getEngineForConnection(connectionId);
    const result = await getTableTriggers(connectionId, database, schema, tableName, engine);

    if (result.error) {
      updateNode(node.id, {
        children: [
          createTableSubfolderErrorNode(
            connectionId, database, schema, tableName, 'triggers-folder', node.id, result.error
          ),
        ],
        isLoading: false,
      });
      return;
    }

    if (result.triggers.length === 0) {
      updateNode(node.id, {
        children: [
          createTableSubfolderEmptyNode(
            connectionId, database, schema, tableName, 'triggers-folder', node.id
          ),
        ],
        isLoading: false,
      });
      return;
    }

    const triggerNodes = result.triggers.map((trigger: TriggerInfo) => {
      const displayName = formatTriggerDisplayName(trigger);
      const metadata: TriggerNodeMetadata = {
        connectionId,
        database,
        schema,
        tableName,
        triggerName: trigger.name,
        timing: trigger.timing,
        events: trigger.events,
        isEnabled: trigger.isEnabled,
      };
      const triggerNode = createTriggerNode(metadata, node.id);
      triggerNode.name = displayName;
      return triggerNode;
    });

    updateNode(node.id, {
      children: triggerNodes,
      isLoading: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load triggers';
    updateNode(node.id, {
      children: [
        createTableSubfolderErrorNode(
          connectionId, database, schema, tableName, 'triggers-folder', node.id, message
        ),
      ],
      isLoading: false,
    });
  }
}
